/* Media components for the docs reader:
 *  - DocImage: responsive (srcset via the backend transform layer), no-layout-shift,
 *    lazy/async, with click-to-zoom lightbox and optional caption.
 *  - YouTubeEmbed / LoomEmbed: click-to-load — a lightweight placeholder that only
 *    loads the player iframe when the reader clicks, so an unwatched video costs nothing.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { X, Play, ZoomIn } from 'lucide-react';
import { useContext } from 'react';
import { DocVariantContext } from './docVariant';

const SRCSET_WIDTHS = [480, 768, 1080, 1600];
const isTransformable = (src) => typeof src === 'string' && src.includes('/api/public/files/');

const withParams = (src, params) => {
  try {
    const [base, q = ''] = src.split('?');
    const usp = new URLSearchParams(q);
    Object.entries(params).forEach(([k, v]) => usp.set(k, v));
    return `${base}?${usp.toString()}`;
  } catch {
    return src;
  }
};

const buildSrcSet = (src) =>
  SRCSET_WIDTHS.map((w) => `${withParams(src, { w, format: 'webp' })} ${w}w`).join(', ');

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ---------- Lightbox ---------- */
const Lightbox = ({ src, alt, onClose }) => {
  const closeRef = useRef(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={alt || 'Image preview'}
      data-testid="image-lightbox"
      onClick={onClose}
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
      style={prefersReducedMotion() ? {} : { animation: 'fadeIn .15s ease-out' }}
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close image preview"
        data-testid="lightbox-close"
        className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
      >
        <X className="h-5 w-5" />
      </button>
      <figure className="max-w-[95vw] max-h-[90vh] flex flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
        <img
          src={isTransformable(src) ? withParams(src, { w: 1600, format: 'webp' }) : src}
          alt={alt || ''}
          className="max-w-full max-h-[82vh] object-contain rounded-lg"
        />
        {alt && alt.trim() && (
          <figcaption className="text-sm text-zinc-300 text-center max-w-2xl">{alt}</figcaption>
        )}
      </figure>
    </div>
  );
};

/* ---------- Responsive, zoomable image ---------- */
export const DocImage = ({ src, alt, caption, eager = false }) => {
  const kb = useContext(DocVariantContext);
  const [ratio, setRatio] = useState(null);
  const [zoom, setZoom] = useState(false);
  const onLoad = useCallback((e) => {
    const { naturalWidth: w, naturalHeight: h } = e.target;
    if (w && h) setRatio(`${w} / ${h}`);
  }, []);
  if (!src) return null;
  const label = caption || alt;
  const transformable = isTransformable(src);

  const img = (
    <img
      src={transformable ? withParams(src, { w: 1080, format: 'webp' }) : src}
      {...(transformable ? { srcSet: buildSrcSet(src), sizes: '(max-width: 768px) 100vw, 768px' } : {})}
      alt={alt || ''}
      onLoad={onLoad}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      style={ratio ? { aspectRatio: ratio } : undefined}
      className={kb ? "rounded-lg max-w-full h-auto" : "w-full h-auto rounded-lg border border-zinc-200 dark:border-zinc-800"}
      data-testid="doc-image"
    />
  );

  return (
    <>
      <figure className="my-6 relative z-10 group">
        <button
          type="button"
          onClick={() => setZoom(true)}
          aria-label={label ? `Zoom image: ${label}` : 'Zoom image'}
          className="block w-full cursor-zoom-in relative"
          data-testid="doc-image-zoom"
        >
          {img}
          <span className="absolute top-2 right-2 h-8 w-8 rounded-md bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <ZoomIn className="h-4 w-4" />
          </span>
        </button>
        {label && label.trim() && (
          <figcaption className={kb ? "mt-2 text-sm text-gray-500 dark:text-[#999999] text-center" : "mt-2 text-sm text-zinc-500 dark:text-zinc-400 text-center"}>{label}</figcaption>
        )}
      </figure>
      {zoom && <Lightbox src={src} alt={label} onClose={() => setZoom(false)} />}
    </>
  );
};

/* ---------- Click-to-load video embeds ---------- */
const ThumbShell = ({ children, testId }) => (
  <div className="my-6 relative z-10" data-testid={testId}>{children}</div>
);

const PlayOverlay = () => (
  <span className="absolute inset-0 flex items-center justify-center">
    <span className="h-16 w-16 rounded-full bg-black/60 group-hover:bg-brand transition-colors flex items-center justify-center">
      <Play className="h-7 w-7 text-white translate-x-0.5" fill="currentColor" />
    </span>
  </span>
);

export const YouTubeEmbed = ({ id, title }) => {
  const [play, setPlay] = useState(false);
  if (!id) return null;
  let videoId = id;
  if (id.includes('youtube.com') || id.includes('youtu.be')) {
    const m = id.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (m) videoId = m[1];
    else {
      const s = id.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
      if (s) videoId = s[1].split('?')[0];
    }
  }
  return (
    <ThumbShell testId="youtube-embed">
      <div className="relative w-full overflow-hidden rounded-lg" style={{ paddingBottom: '56.25%' }}>
        {play ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
            title={title || 'Video'}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlay(true)}
            aria-label={`Play video: ${title || 'YouTube video'}`}
            data-testid="youtube-play"
            className="group absolute inset-0 w-full h-full cursor-pointer"
          >
            <img
              src={`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`}
              alt={title || 'Video thumbnail'}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <PlayOverlay />
          </button>
        )}
      </div>
      {title && title.trim() && (
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 text-center">{title}</p>
      )}
    </ThumbShell>
  );
};

export const LoomEmbed = ({ id, title }) => {
  const [play, setPlay] = useState(false);
  if (!id) return null;
  let loomId = id;
  if (id.includes('loom.com')) {
    const m = id.match(/loom\.com\/(?:share|embed)\/([a-zA-Z0-9]+)/);
    if (m) loomId = m[1];
  }
  return (
    <ThumbShell testId="loom-embed">
      <div className="relative w-full overflow-hidden rounded-lg bg-zinc-900" style={{ paddingBottom: '56.25%' }}>
        {play ? (
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`https://www.loom.com/embed/${loomId}?autoplay=1`}
            title={title || 'Video'}
            frameBorder="0"
            allowFullScreen
          />
        ) : (
          <button
            type="button"
            onClick={() => setPlay(true)}
            aria-label={`Play video: ${title || 'Loom video'}`}
            data-testid="loom-play"
            className="group absolute inset-0 w-full h-full cursor-pointer bg-gradient-to-br from-[#625df5]/30 to-[#1588FC]/20"
          >
            <span className="absolute top-3 left-3 text-xs font-semibold text-white/80">Loom</span>
            <PlayOverlay />
          </button>
        )}
      </div>
      {title && title.trim() && (
        <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400 text-center">{title}</p>
      )}
    </ThumbShell>
  );
};
